/**
 * @architecture-header 1
 * @layer app
 * @domain workspace
 * @responsibility Define host-independent workspace and organization-student identity entry routes.
 * @state-owner React Router path is the entry point; the navigation store owns in-product movement
 * @allowed-imports routes and auth public APIs; app session provider; React Router
 * @forbidden browser history implementation; Electron history; module internals
 * @public-api createWorkspaceRoutes via @vispo/workspace-web
 * @side-effects route rendering only
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { Navigate, type RouteObject, useNavigate } from 'react-router-dom';
import { type OrganizationSession, OrganizationStudentAuthScreen } from '../../modules/auth';
import { WorkspaceShell } from '../../routes/WorkspaceShell';
import { useWorkspaceSession } from '../providers/SessionProvider';

const ORGANIZATION_LEGAL_VERSION = {
  consentRequired: true,
  privacyVersion: 'privacy-v1',
  termsVersion: 'terms-v1',
} as const;

function OrganizationIdentityEntry({ mode }: { mode: 'login' | 'activate' }) {
  const navigate = useNavigate();
  const { acceptOrganizationSession, isLoading, session } = useWorkspaceSession();

  if (isLoading) return null;
  if (session) return <Navigate replace to="/" />;

  const completeAuthentication = async (organizationSession: OrganizationSession) => {
    acceptOrganizationSession(organizationSession);
    await navigate('/', { replace: true });
  };

  return (
    <OrganizationStudentAuthScreen
      initialMode={mode}
      legal={ORGANIZATION_LEGAL_VERSION}
      onAuthenticated={completeAuthentication}
    />
  );
}

/**
 * The  product is a single shell whose section, selection and overlays live in the
 * navigation store, not the URL. The paths below only seed the entry
 * section so deep links keep working.
 */
export function createWorkspaceRoutes(): RouteObject[] {
  return [
    { path: '/', element: <WorkspaceShell /> },
    { path: '/student/org-login', element: <OrganizationIdentityEntry mode="login" /> },
    { path: '/student/org-activate', element: <OrganizationIdentityEntry mode="activate" /> },
    { path: '/activities', element: <WorkspaceShell /> },
    { path: '/classes', element: <WorkspaceShell /> },
    { path: '/live', element: <WorkspaceShell /> },
    { path: '/reports', element: <WorkspaceShell /> },
    { path: '*', element: <WorkspaceShell /> },
  ];
}
